/* allTogether – Chor Nalbach */

@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Nunito:wght@400;600;700;800&display=swap');

:root{
  --bg: #071a1e;
  --bg2:#0b2a30;
  --card:#0f2f36;
  --text:#e9f5f7;
  --muted:#b7d5db;
  --brand:#22b8c7;
  --brand2:#12a4b3;
  --brand-glow: rgba(34,184,199,.12);
  --warm: #e8c87a;
  --warm2: #d4a94e;
  --shadow: 0 18px 60px rgba(0,0,0,.35);
  --radius: 18px;
  --font-display: 'DM Serif Display', Georgia, 'Times New Roman', serif;
  --font-body: 'Nunito', system-ui, -apple-system, sans-serif;
}

*{ box-sizing:border-box; }
html{ scroll-behavior:smooth; }

body{
  margin:0;
  font-family: var(--font-body);
  color:var(--text);
  background:
    radial-gradient(1200px 800px at 20% 0%, #103e46 0%, var(--bg) 55%),
    linear-gradient(180deg, var(--bg), #061114);
  background-attachment: fixed;
}

a{ color:var(--text); transition: color .2s; }
a:hover{ color:var(--brand); }

.container{ width:min(1060px, calc(100% - 40px)); margin:0 auto; }

/* ─── HEADER ─── */
.header{
  position:sticky;
  top:0;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  background: rgba(7, 26, 30, .72);
  border-bottom: 1px solid rgba(34,184,199,.12);
  z-index:20;
}
.header-inner{
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 0; gap:16px;
}
.brand{
  display:flex; align-items:center; gap:10px;
  text-decoration:none; font-weight:800;
  font-family: var(--font-display);
  font-size: 18px;
  letter-spacing:.3px;
}
.dot{
  width:10px; height:10px; border-radius:999px;
  background: var(--brand);
  box-shadow: 0 0 0 6px var(--brand-glow);
  animation: pulse 3s ease-in-out infinite;
}
@keyframes pulse{
  0%,100%{ box-shadow: 0 0 0 6px var(--brand-glow); }
  50%{ box-shadow: 0 0 0 10px rgba(34,184,199,.06); }
}

.nav{ display:flex; gap:14px; flex-wrap:wrap; }
.nav a{
  text-decoration:none; color:var(--muted); font-weight:700;
  font-size:13px; letter-spacing:.02em;
  position:relative; padding-bottom:2px;
}
.nav a::after{
  content:''; position:absolute; bottom:0; left:0;
  width:0; height:2px; background:var(--brand);
  border-radius:2px; transition: width .3s;
}
.nav a:hover{ color:var(--text); }
.nav a:hover::after{ width:100%; }

/* Mobile nav toggle */
.nav-toggle{
  display:none; background:none; border:none; cursor:pointer;
  flex-direction:column; gap:5px; padding:6px;
}
.nav-toggle span{
  display:block; width:22px; height:2px;
  background:var(--muted); border-radius:2px;
  transition: .3s;
}

@media (max-width: 700px){
  .nav-toggle{ display:flex; }
  .nav{
    display:none; position:absolute; top:100%; left:0; right:0;
    flex-direction:column; gap:0;
    background: rgba(7, 26, 30, .95);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(34,184,199,.12);
    padding: 8px 20px 16px;
  }
  .nav.open{ display:flex; }
  .nav a{ padding:10px 0; font-size:15px; }
  .nav a::after{ display:none; }
}

/* ─── HERO ─── */
.hero{ padding:56px 0 26px; }
.hero-grid{
  display:grid; grid-template-columns: 1.15fr .85fr;
  gap:26px; align-items:stretch;
}
@media (max-width: 920px){ .hero-grid{ grid-template-columns: 1fr; } }

.card{
  background: linear-gradient(180deg, rgba(15,47,54,.92), rgba(10,30,35,.92));
  border: 1px solid rgba(34,184,199,.15);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow:hidden;
}

.hero-copy{ padding:32px 28px; }
.kicker{
  display:inline-flex; align-items:center; gap:10px;
  color:var(--muted); font-weight:800;
  letter-spacing:.14em; text-transform:uppercase; font-size:11px;
}
.kicker .line{
  width:26px; height:2px;
  background: rgba(34,184,199,.7); border-radius:999px;
}

h1{
  margin:14px 0 16px; font-size:48px; line-height:1.05;
  font-family: var(--font-display);
}
@media (max-width: 520px){ h1{ font-size:36px; } }

p{ color:var(--muted); line-height:1.7; margin:0 0 14px; }

.hero-lead{
  font-size:16px; color:var(--text); line-height:1.75;
  margin-bottom:12px;
}
.hero-sub{
  font-size:14px; color:var(--muted); line-height:1.75;
}

.cta-row{ display:flex; gap:12px; flex-wrap:wrap; margin-top:22px; }
.btn{
  display:inline-flex; align-items:center; justify-content:center;
  padding:12px 22px;
  border-radius: 999px;
  text-decoration:none;
  font-weight:800; font-size:14px;
  border: 1px solid rgba(34,184,199,.35);
  transition: all .25s;
}
.btn-primary{
  background: linear-gradient(180deg, var(--brand), var(--brand2));
  color:#052226; border:none;
}
.btn-primary:hover{ filter: brightness(1.08); color:#052226; transform:translateY(-1px); }
.btn-ghost{ background: rgba(255,255,255,.02); color:var(--text); }
.btn-ghost:hover{ background: rgba(34,184,199,.10); transform:translateY(-1px); }

.hero-photo{
  min-height: 360px;
  background:
    radial-gradient(800px 380px at 30% 20%, rgba(34,184,199,.25), transparent 60%),
    radial-gradient(600px 420px at 90% 10%, rgba(34,184,199,.18), transparent 55%),
    linear-gradient(180deg, rgba(255,255,255,.03), rgba(0,0,0,.06));
  display:flex;
}
.hero-photo .ph{
  flex:1;
  opacity:.95;
  position:relative;
  overflow:hidden;
}
.hero-photo .ph img{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
}
.hero-photo .ph.placeholder{
  background-image: linear-gradient(135deg, rgba(34,184,199,.20), rgba(255,255,255,.06));
  display:flex; align-items:flex-end;
}
.hero-photo .ph .label{
  position:absolute; bottom:0; left:0; right:0;
  padding:18px 20px;
  background: linear-gradient(180deg, transparent, rgba(0,0,0,.6));
  color: rgba(233,245,247,.9);
  font-weight:700; font-size:13px;
}

/* ─── SECTIONS ─── */
.section{ padding:40px 0; }

.section-title{
  font-family: var(--font-display);
  font-size:32px; font-weight:400;
  margin:0 0 28px; display:flex; align-items:center; gap:14px;
}
.section-line{
  width:32px; height:3px;
  background: linear-gradient(90deg, var(--brand), var(--warm));
  border-radius:3px; flex-shrink:0;
}

/* ─── STORY / ÜBER UNS ─── */
.story-grid{
  display:grid; grid-template-columns: repeat(3, 1fr); gap:18px;
}
@media (max-width: 920px){ .story-grid{ grid-template-columns: 1fr; } }

.story-card{
  padding:28px 24px;
  position:relative;
  transition: transform .3s, border-color .3s;
}
.story-card:hover{
  transform:translateY(-3px);
  border-color: rgba(34,184,199,.3);
}
.story-year{
  font-family: var(--font-display);
  font-size:40px; color:var(--brand);
  opacity:.35; line-height:1; margin-bottom:8px;
}
.story-card h3{
  margin:0 0 10px; font-size:17px; font-weight:800;
  color:var(--text);
}
.story-card p{
  margin:0; font-size:14px; line-height:1.75;
}

/* ─── TERMINE ─── */
.termine-grid{
  display:grid; grid-template-columns: repeat(2, 1fr); gap:18px;
}
@media (max-width: 700px){ .termine-grid{ grid-template-columns: 1fr; } }

.termine-card{
  padding:28px 24px;
}
.termine-jahr{
  font-family: var(--font-display);
  font-size:36px; color:var(--warm);
  margin-bottom:18px; line-height:1;
}
.termine-list{
  list-style:none; margin:0; padding:0;
}
.termine-list li{
  display:flex; gap:12px; align-items:baseline;
  padding:10px 0;
  border-bottom: 1px solid rgba(34,184,199,.08);
  transition: background .2s;
}
.termine-list li:last-child{ border-bottom:none; }
.termine-list li:hover{
  background: rgba(34,184,199,.04);
  margin: 0 -12px; padding-left:12px; padding-right:12px;
  border-radius:8px;
}
.termin-datum{
  font-weight:800; font-size:14px; color:var(--brand);
  min-width:50px; flex-shrink:0;
  font-variant-numeric: tabular-nums;
}
.termin-text{
  font-size:14px; color:var(--muted); line-height:1.5;
}
.termin-past{
  opacity:.5;
}
.termin-past .termin-datum{
  color:var(--muted);
}

/* ─── AKTUELLES ─── */
.aktuell-stack{
  display:grid; gap:18px;
}
.aktuell-card{
  padding:36px 32px;
  text-align:center;
  background:
    radial-gradient(600px 300px at 50% 0%, rgba(232,200,122,.10), transparent),
    linear-gradient(180deg, rgba(15,47,54,.92), rgba(10,30,35,.92));
  border-color: rgba(232,200,122,.28);
  position:relative;
  overflow:hidden;
}
.aktuell-card::before{
  content:'';
  position:absolute; top:-60px; left:50%; transform:translateX(-50%);
  width:340px; height:340px;
  background: radial-gradient(circle, rgba(232,200,122,.07), transparent 70%);
  pointer-events:none;
}
.aktuell-badge{
  display:inline-block;
  background: rgba(232,200,122,.10);
  border: 1px solid rgba(232,200,122,.32);
  color:var(--warm);
  font-size:11px; font-weight:800;
  letter-spacing:.14em; text-transform:uppercase;
  padding:4px 14px; border-radius:999px;
  margin-bottom:14px;
}
.aktuell-icon{
  font-size:38px; line-height:1; margin-bottom:8px;
  color:var(--warm); opacity:.65;
}
.aktuell-title{
  font-family: var(--font-display);
  font-size:28px; margin:0 0 14px; color:var(--text);
}
.aktuell-text{
  font-size:15px; color:var(--muted);
  max-width:520px; margin:0 auto; line-height:1.8;
}
.aktuell-text strong{
  color:var(--warm); font-weight:800;
}
.aktuell-plakat{
  display:block; width:min(280px, 100%);
  margin:22px auto 0;
  position:relative;
}
.aktuell-plakat img{
  display:block; width:100%; height:auto;
  border-radius:14px;
  border:1px solid rgba(232,200,122,.28);
  box-shadow: 0 12px 32px rgba(0,0,0,.35);
  transition: transform .2s;
}
.aktuell-plakat:hover img{ transform: translateY(-2px); }

/* ─── PROBE ─── */
.probe-card{
  padding:36px 32px;
  text-align:center;
  background:
    radial-gradient(600px 300px at 50% 0%, rgba(34,184,199,.12), transparent),
    linear-gradient(180deg, rgba(15,47,54,.92), rgba(10,30,35,.92));
  border-color: rgba(34,184,199,.22);
  position:relative;
  overflow:hidden;
}
.probe-card::before{
  content:'';
  position:absolute; top:-60px; left:50%; transform:translateX(-50%);
  width:300px; height:300px;
  background: radial-gradient(circle, rgba(232,200,122,.06), transparent 70%);
  pointer-events:none;
}
.probe-icon{
  font-size:42px; line-height:1; margin-bottom:8px;
  color:var(--warm); opacity:.7;
}
.probe-card h2{
  font-family: var(--font-display);
  font-size:28px; margin:0 0 16px;
}
.probe-detail{
  font-size:18px; color:var(--text); margin-bottom:6px;
}
.probe-ort{
  font-size:15px; color:var(--brand); font-weight:700;
  margin-bottom:16px;
}
.probe-hinweis{
  font-size:14px; color:var(--muted);
  max-width:400px; margin:0 auto;
}

/* ─── KONTAKT ─── */
.kontakt-grid{
  display:grid; grid-template-columns: repeat(2, 1fr); gap:18px;
}
@media (max-width: 700px){ .kontakt-grid{ grid-template-columns: 1fr; } }

.kontakt-card{
  padding:28px 24px;
  transition: transform .3s, border-color .3s;
}
.kontakt-card:hover{
  transform:translateY(-3px);
  border-color: rgba(34,184,199,.3);
}
.kontakt-name{
  font-family: var(--font-display);
  font-size:22px; margin-bottom:4px;
}
.kontakt-rolle{
  font-size:13px; color:var(--brand); font-weight:700;
  text-transform:uppercase; letter-spacing:.08em;
  margin-bottom:18px;
}
.kontakt-details{
  display:flex; flex-direction:column; gap:10px;
}
.kontakt-link{
  display:flex; align-items:center; gap:10px;
  text-decoration:none; color:var(--muted);
  font-size:14px; font-weight:600;
  padding:8px 12px;
  border-radius:10px;
  background: rgba(34,184,199,.04);
  border: 1px solid rgba(34,184,199,.08);
  transition: all .25s;
}
.kontakt-link:hover{
  background: rgba(34,184,199,.10);
  border-color: rgba(34,184,199,.2);
  color:var(--text);
}
.kontakt-icon{
  font-size:16px; color:var(--brand); opacity:.7;
  width:20px; text-align:center; flex-shrink:0;
}

/* ─── INFO GRID (legacy) ─── */
.grid{ display:grid; grid-template-columns: repeat(3, 1fr); gap:14px; }
@media (max-width: 920px){ .grid{ grid-template-columns: 1fr; } }

.mini{ padding:18px; }
.mini h3{ margin:0 0 6px; font-size:16px; }
.mini p{ margin:0; font-size:14px; }

/* ─── FOOTER ─── */
.footer{
  padding:26px 0 44px;
  color: rgba(183,213,219,.85);
  border-top: 1px solid rgba(34,184,199,.08);
  margin-top:20px;
}
.footer a{ color: rgba(183,213,219,.95); }

/* ─── REVEAL ANIMATION ─── */
.reveal{
  transform: translateY(16px);
  opacity:0;
  transition: transform .7s cubic-bezier(.23,1,.32,1), opacity .7s ease;
}
.reveal.in{ transform:none; opacity:1; }

/* Stagger reveals */
.story-card:nth-child(2) .reveal,
.story-card:nth-child(2).reveal{ transition-delay:.1s; }
.story-card:nth-child(3) .reveal,
.story-card:nth-child(3).reveal{ transition-delay:.2s; }

/* ─── UTILS ─── */
.small{ font-size:13px; color: rgba(183,213,219,.9); }
hr.sep{ border:none; height:1px; background: rgba(34,184,199,.14); margin:18px 0; }

/* ─── SUBTLE TEXTURE ─── */
body::after{
  content:'';
  position:fixed; top:0; left:0; right:0; bottom:0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  opacity:.02;
  pointer-events:none;
  z-index:0;
}

main{ position:relative; z-index:1; }
.header{ position:sticky; z-index:20; }
